
import { Check, Monitor, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { secureOpenUrl } from '@/utils/linkValidation';

const ResidentialRDPPricingSection = () => {
  const rdpPlans = [
    {
      name: "Basic RDP",
      cpu: "2 vCPU",
      ram: "4GB RAM",
      storage: "30GB SSD",
      price: "$15.00",
      monthly: true,
      link: "https://acc.nexx.net/index.php?rp=/store/residential-remote-desktops/basic-rdp",
      popular: false
    },
    {
      name: "Standard RDP",
      cpu: "4 vCPU", 
      ram: "8GB RAM",
      storage: "35GB SSD",
      price: "$30.00",
      monthly: true,
      link: "https://acc.nexx.net/index.php?rp=/store/residential-remote-desktops/standard-rdp",
      popular: true
    },
    {
      name: "Premium RDP",
      cpu: "6 vCPU",
      ram: "16GB RAM", 
      storage: "40GB SSD",
      price: "$60.00",
      monthly: true,
      link: "https://acc.nexx.net/index.php?rp=/store/residential-remote-desktops/residential-premium-rdp",
      popular: false
    }
  ];

  const RDPCard = ({ rdp, index }: { rdp: any, index: number }) => (
    <Card key={rdp.name} className={`group relative border-gray-200 hover:shadow-2xl hover:shadow-nexx-green/30 hover:border-nexx-green transition-all duration-300 hover:scale-105 hover:z-50 ${rdp.popular ? 'border-2 border-nexx-green shadow-xl ring-4 ring-nexx-green/10' : ''}`}>
      {rdp.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-nexx-green text-white px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </div>
        </div>
      )}
      
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-bold text-center">{rdp.name}</CardTitle>
        <CardDescription className="text-center">
          <span className="text-2xl font-bold text-nexx-green">{rdp.price}</span>
          {rdp.monthly && <span className="text-sm text-gray-500">/month</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            {rdp.cpu}
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            {rdp.ram}
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            {rdp.storage}
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            <span className="font-semibold text-nexx-green">Residential US IP</span>
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            Windows 10/11 Pro
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            Full Admin Access
          </div>
          <div className="flex items-center text-sm">
            <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
            24/7 Support
          </div>
        </div>
        
        <Button 
          className={`w-full mt-6 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-nexx-green/50 ${rdp.popular ? 'bg-nexx-green hover:bg-green-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white group-hover:bg-nexx-green'}`}
          onClick={() => secureOpenUrl(rdp.link)}
        >
          Order Now
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <section className="py-16 bg-gray-50 relative z-10 overflow-visible">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Residential RDP Plans</h2>
          <p className="text-xl text-gray-600">
            Powerful remote desktops with authentic{' '}
            <span className="relative inline-block text-nexx-green font-semibold">
              US residential IP addresses
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-nexx-green to-green-600 transform scale-x-0 animate-[scale-x-100_2s_ease-out_0.5s_forwards] origin-left"></span>
            </span>{' '}
            for seamless access.
          </p>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-r from-nexx-green to-green-600 rounded-2xl px-8 py-4 shadow-lg transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 rounded-xl p-3">
                  <Monitor className="h-8 w-8 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-white">Residential Remote Desktops</h3>
                  <p className="text-green-100 text-sm font-medium">US Residential IPs • Windows Pro • Full Admin</p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 relative z-20">
            {rdpPlans.map((rdp, index) => (
              <RDPCard key={rdp.name} rdp={rdp} index={index} />
            ))}
          </div>
        </div>

        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">
            All plans include <span className="font-semibold text-nexx-green">authentic US residential IPs</span> and <span className="font-semibold text-nexx-green">instant setup</span>.
          </p>
          <div className="inline-flex items-center px-4 py-2 bg-green-50 rounded-full text-nexx-green text-sm font-medium border border-green-200">
            <Shield className="w-4 h-4 mr-2" />
            Perfect for ad verification, social media management, and remote development
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResidentialRDPPricingSection;
